<template>
  <article
    :id="project.title"
    :class="['project-card', { 'project-card--standalone': standalone }]"
    data-test="project-list-item"
  >
    <div class="project-card__visual">
      <router-link
        :to="cardRoute"
        class="project-card__thumb"
      >
        <img
          :src="thumbnailSrc"
          alt="Thumbnail du projet"
        >
      </router-link>
      <div class="project-card__stats">
        <span
          data-tooltip="Membres"
          :aria-label="`${project.nb_contributors} membres`"
        >
          {{ project.nb_contributors }}
          <img
            class="stat-icon"
            :src="icons.user"
            alt=""
          >
        </span>
        <span
          data-tooltip="Signalements"
          :aria-label="`${project.nb_published_features} signalements`"
        >
          {{ project.nb_published_features }}
          <img
            class="stat-icon"
            :src="icons.feature"
            alt=""
          >
        </span>
        <span
          data-tooltip="Commentaires"
          :aria-label="`${project.nb_published_features_comments} commentaires`"
        >
          {{ project.nb_published_features_comments }}
          <img
            class="stat-icon"
            :src="icons.comment"
            alt=""
          >
        </span>
      </div>
    </div>

    <div class="project-card__body">
      <div class="project-card__main">
        <div class="project-card__copy">
          <router-link
            :to="cardRoute"
            class="project-card__title"
          >
            {{ project.title }}
          </router-link>
          <p class="project-card__date">
            {{ relativeDateLabel }}
          </p>
          <div class="project-card__description">
            <textarea
              :id="`editor-${project.slug}`"
              :value="project.description"
              :data-preview="`#preview-${project.slug}`"
              hidden
            />
            <div
              :id="`preview-${project.slug}`"
              class="preview"
            />
          </div>
        </div>
        <div class="project-card__actions">
          <button
            type="button"
            class="action-btn"
            data-tooltip="Partager le lien des signalements"
            aria-label="Partager le lien des signalements"
            @click="copyFeaturesLink"
          >
            <img
              :src="icons.share"
              alt=""
            >
          </button>
          <button
            type="button"
            class="action-btn"
            data-tooltip="Partager le code du web component"
            aria-label="Partager le code du web component"
            @click="copyFeaturesCode"
          >
            <img
              :src="icons.code"
              alt=""
            >
          </button>
          <router-link
            v-if="showAdminActions"
            :to="{ name: 'project_members', params: { slug: project.slug } }"
            class="action-btn"
            data-tooltip="Membres du projet"
          >
            <img
              :src="icons.members"
              alt=""
            >
          </router-link>
          <router-link
            v-if="showAdminActions && isOnline"
            :to="{ name: 'project_edit', params: { slug: project.slug } }"
            class="action-btn"
            data-tooltip="Modifier le projet"
          >
            <img
              :src="icons.edit"
              alt=""
            >
          </router-link>
          <button
            v-if="showAdminActions && isOnline"
            type="button"
            class="action-btn"
            data-tooltip="Supprimer le projet"
            aria-label="Supprimer le projet"
            @click="$emit('open-modal', { type: 'deleteProject', project })"
          >
            <img
              :src="icons.remove"
              alt=""
            >
          </button>
          <button
            v-if="showNotification"
            type="button"
            class="action-btn"
            data-tooltip="Gérer mon abonnement au projet"
            aria-label="Gérer mon abonnement au projet"
            @click="$emit('open-modal', { type: 'subscribe', project })"
          >
            <img
              :src="project.is_suscriber ? icons.notifyOn : icons.notify"
              alt=""
            >
          </button>
        </div>
      </div>

      <div class="project-card__badges">
        <span
          v-if="roleBadge"
          class="status-badge"
          :style="{ background: roleBadge.color }"
        >
          <img
            class="badge-icon"
            :src="icons.user"
            alt=""
          >
          {{ roleBadge.label }}
        </span>
        <span
          v-if="showConsultation"
          class="status-badge tone-consultation"
        >
          <img
            class="badge-icon"
            :src="isPrivateConsultation ? icons.tagPrivate : icons.tagPublic"
            alt=""
          >
          {{ isPrivateConsultation ? 'Consultation privée' : 'Consultation publique' }}
        </span>
        <div
          v-if="showModeration || attributeTags.length"
          class="badge-row"
        >
          <span
            v-if="showModeration"
            class="status-badge tone-moderation"
          >
            <img
              class="badge-icon"
              :src="icons.tagModeration"
              alt=""
            >
            {{ project.moderation ? 'Modéré' : 'Non modéré' }}
          </span>
          <span
            v-for="attribute in attributeTags"
            :key="`${project.slug}-attr-${attribute.id}`"
            class="status-badge tone-attribute"
          >
            <img
              class="badge-icon"
              :src="icons.tagAttribute"
              alt=""
            >
            {{ attribute.label }}
          </span>
        </div>
        <div
          v-if="infoTags.length"
          class="badge-row"
        >
          <span
            v-for="tag in infoTags"
            :key="`${project.slug}-${tag.key}`"
            :class="['status-badge', tag.tone]"
          >
            {{ tag.count }} {{ tag.label }}
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="infoMessage"
      class="copy-modal"
      @click.self="infoMessage = ''"
    >
      <div
        class="copy-modal__panel"
        role="dialog"
        aria-modal="true"
      >
        <p>{{ infoMessage }}</p>
        <button
          type="button"
          class="ui compact button"
          @click="infoMessage = ''"
        >
          Fermer
        </button>
      </div>
    </div>
  </article>
</template>

<script>
import TextareaMarkdown from 'textarea-markdown';

import { mapState } from 'vuex';

import shareIcon from '@/assets/img/project-card/share.png';
import codeIcon from '@/assets/img/project-card/code.png';
import membersIcon from '@/assets/img/project-card/members.png';
import editIcon from '@/assets/img/project-card/edit.png';
import deleteIcon from '@/assets/img/project-card/delete.png';
import notifyIcon from '@/assets/img/project-card/notify.png';
import notifyOnIcon from '@/assets/img/project-card/notify-on.png';
import statUserIcon from '@/assets/img/project-card/stat-user.png';
import statFeatureIcon from '@/assets/img/project-card/stat-feature.png';
import statCommentIcon from '@/assets/img/project-card/stat-comment.png';
import tagModerationIcon from '@/assets/img/project-card/tag-moderation.png';
import tagPublicIcon from '@/assets/img/project-card/tag-public.png';
import tagPrivateIcon from '@/assets/img/project-card/tag-private.png';
import tagAttributeIcon from '@/assets/img/project-card/tag-attribute.png';

function normalizeRole(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

const ROLE_TAGS = {
  Lecteur: { label: 'Lecteur', color: '#5E72E6', key: 'reader' },
  Contributeur: { label: 'Contributeur', color: '#4D5EBD', key: 'contributor' },
  'Super Contributeur': { label: 'Super contributeur', color: '#3A468E', key: 'super' },
  'Super contributeur': { label: 'Super contributeur', color: '#3A468E', key: 'super' },
  Modérateur: { label: 'Modérateur', color: '#2C356B', key: 'moderator' },
  'Administrateur projet': { label: 'Administrateur', color: '#1B2142', key: 'admin' },
  Administrateur: { label: 'Administrateur', color: '#1B2142', key: 'admin' },
};

export default {

  name: 'ProjectsListItem',

  props: {
    project: {
      type: Object,
      default: () => {
        return {};
      }
    },
    standalone: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      infoMessage: '',
      icons: {
        share: shareIcon,
        code: codeIcon,
        members: membersIcon,
        edit: editIcon,
        remove: deleteIcon,
        notify: notifyIcon,
        notifyOn: notifyOnIcon,
        user: statUserIcon,
        feature: statFeatureIcon,
        comment: statCommentIcon,
        tagModeration: tagModerationIcon,
        tagPublic: tagPublicIcon,
        tagPrivate: tagPrivateIcon,
        tagAttribute: tagAttributeIcon,
      },
    };
  },

  computed: {
    ...mapState([
      'USER_LEVEL_PROJECTS',
      'user',
      'isOnline',
      'configuration',
      'projectAttributes',
    ]),
    DJANGO_BASE_URL() {
      return this.configuration.VUE_APP_DJANGO_BASE;
    },
    thumbnailSrc() {
      if (!this.project.thumbnail || String(this.project.thumbnail).includes('default')) {
        return require('@/assets/img/default.png');
      }
      return this.DJANGO_BASE_URL + this.project.thumbnail + this.refreshId();
    },
    relativeDateLabel() {
      const rawDate = this.project.updated_on || this.project.created_on;
      if (!rawDate) return '';
      const relative = this.formatRelativeDate(rawDate);
      if (relative) return relative;
      return `Créé le ${this.project.created_on}`;
    },
    isGlobalAdmin() {
      //* seul le super-utilisateur Django a accès à tous les projets sans rôle projet ;
      //* le gestionnaire métier suit ses rôles projet comme le statut équipe
      return !!(this.user && this.user.is_superuser);
    },
    projectRole() {
      if (this.isGlobalAdmin) return 'admin';
      if (!this.user || !this.USER_LEVEL_PROJECTS || !this.project) return 'anonymous';
      return (this.roleBadge && this.roleBadge.key) || 'logged';
    },
    cardRoute() {
      const opensProjectHome = ['contributor', 'super', 'moderator', 'admin'].includes(this.projectRole);
      return {
        name: opensProjectHome ? 'project_detail' : 'liste-signalements',
        params: { slug: this.project.slug },
      };
    },
    showNotification() {
      return this.projectRole !== 'anonymous' && this.isOnline;
    },
    showAdminActions() {
      return this.projectRole === 'admin';
    },
    canSeeRoleBadge() {
      //* Actif, statut équipe et gestionnaire métier. Pas l'anonyme ni le super-utilisateur.
      return !!(this.user && !this.user.is_superuser);
    },
    roleBadge() {
      if (!this.canSeeRoleBadge) return null;
      if (!this.USER_LEVEL_PROJECTS || !this.project) return null;
      const level = this.USER_LEVEL_PROJECTS[this.project.slug];
      if (!level) return null;
      if (ROLE_TAGS[level]) return ROLE_TAGS[level];
      const normalized = normalizeRole(level);
      if (normalized === 'lecteur' || normalized === 'reader') return ROLE_TAGS.Lecteur;
      return Object.values(ROLE_TAGS).find((tag) => normalizeRole(tag.label) === normalized)
        || Object.values(ROLE_TAGS).find((tag) => normalizeRole(tag.key) === normalized)
        || null;
    },
    showConsultation() {
      return this.projectRole !== 'anonymous';
    },
    showModeration() {
      return this.projectRole !== 'anonymous';
    },
    isPrivateConsultation() {
      return this.project.access_level_pub_feature !== 'Utilisateur anonyme';
    },
    attributeTags() {
      const associations = this.project.project_attributes || [];
      const tags = [];
      associations.forEach((association) => {
        const raw = association.value == null ? '' : String(association.value).trim();
        if (!raw) return;
        const attribute = (this.projectAttributes || [])
          .find((item) => String(item.id) === String(association.attribute_id));
        if (!attribute) return;
        if (attribute.field_type === 'boolean') {
          //* pour un attribut booléen, c'est le nom de l'attribut qui sert de tag (si coché)
          if (raw === 'true') {
            tags.push({
              id: `${association.attribute_id}-bool`,
              label: attribute.label,
            });
          }
          return;
        }
        if (!['list', 'multi_choices_list'].includes(attribute.field_type)) return;
        //* pour un attribut à valeur(s), ce sont les valeurs qui servent de tag, pas l'attribut
        this.attributeOptionLabels(attribute, raw).forEach((label) => {
          tags.push({
            id: `${association.attribute_id}-${label}`,
            label,
          });
        });
      });
      return tags;
    },
    infoTags() {
      const tags = [];
      const role = this.projectRole;
      if (role === 'admin') {
        tags.push({
          key: 'archived',
          label: 'Archive(s)',
          count: this.project.nb_archived_features || 0,
          tone: 'tone-info',
        });
      }
      if (role === 'contributor' || role === 'super' || role === 'admin') {
        tags.push({
          key: 'draft',
          label: 'Brouillon(s)',
          count: this.draftCount(role),
          tone: 'tone-draft',
        });
      }
      if ((role === 'moderator' || role === 'admin') && this.project.moderation) {
        tags.push({
          key: 'pending',
          label: 'A modérer',
          count: this.project.nb_pending_features || 0,
          tone: 'tone-pending',
        });
      }
      return tags;
    },
  },

  mounted() {
    const textarea = document.getElementById(`editor-${this.project.slug}`);
    new TextareaMarkdown(textarea);
  },

  methods: {
    attributeOptionLabels(attribute, raw) {
      const tokens = this.splitAttributeValue(raw, attribute.field_type);
      const options = Array.isArray(attribute.options) ? attribute.options : [];
      return tokens.map((token) => {
        const option = options.find((item) => {
          if (item && typeof item === 'object') {
            return String(item.id) === token
              || String(item.value) === token
              || item.name === token
              || item.label === token;
          }
          return String(item) === token;
        });
        if (option && typeof option === 'object') return option.name || option.label || token;
        if (option) return String(option);
        return token;
      }).filter(Boolean);
    },

    splitAttributeValue(raw, fieldType) {
      if (fieldType !== 'multi_choices_list') return [raw];
      if (raw.startsWith('[')) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            return parsed.map((value) => String(value).trim()).filter(Boolean);
          }
        } catch (error) {
          return raw.split(',').map((value) => value.trim()).filter(Boolean);
        }
      }
      return raw.split(',').map((value) => value.trim()).filter(Boolean);
    },

    draftCount(role) {
      const drafts = role === 'contributor'
        ? this.project.nb_my_draft_features
        : this.project.nb_draft_features;
      const pending = this.project.moderation ? 0 : (this.project.nb_pending_features || 0);
      return (drafts || 0) + (role === 'contributor' ? 0 : pending);
    },

    refreshId() {
      const crypto = window.crypto || window.msCrypto;
      var array = new Uint32Array(1);
      return '?ver=' + crypto.getRandomValues(array);
    },

    formatRelativeDate(rawDate) {
      const date = this.parseDate(rawDate);
      if (!date) return null;

      const diffMs = Date.now() - date.getTime();
      if (Number.isNaN(diffMs) || diffMs < 0) return null;

      //* l'API ne renvoie que le jour pour les projets, compter des heures donnerait un écart fictif
      const days = this.diffInDays(date);
      if (days === 0) return 'Mise à jour aujourd\'hui';
      if (days === 1) return 'Mise à jour hier';
      if (days < 30) {
        return `Mise à jour il y a ${days} jours`;
      }
      const months = Math.floor(days / 30);
      if (months < 12) {
        return months === 1 ? 'Mise à jour il y a 1 mois' : `Mise à jour il y a ${months} mois`;
      }
      const years = Math.floor(months / 12);
      return years === 1 ? 'Mise à jour il y a 1 an' : `Mise à jour il y a ${years} ans`;
    },

    diffInDays(date) {
      const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      return Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
    },

    parseDate(rawDate) {
      if (!rawDate) return null;
      if (rawDate instanceof Date) return rawDate;

      // Support common FR formats like DD/MM/YYYY
      //* à tester avant new Date(), qui interpréterait "03/08/2026" comme le 8 mars
      const frMatch = String(rawDate).match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
      if (frMatch) {
        const day = Number(frMatch[1]);
        const month = Number(frMatch[2]) - 1;
        const year = Number(frMatch[3].length === 2 ? `20${frMatch[3]}` : frMatch[3]);
        const parsed = new Date(year, month, day);
        if (!Number.isNaN(parsed.getTime())) return parsed;
      }

      const isoAttempt = new Date(rawDate);
      if (!Number.isNaN(isoAttempt.getTime())) return isoAttempt;

      return null;
    },

    copyFeaturesLink() {
      const url = this.$router.resolve({
        name: 'liste-signalements',
        params: { slug: this.project.slug },
      }).href;
      const absoluteUrl = `${window.location.origin}${url}`;
      navigator.clipboard.writeText(absoluteUrl).then(() => {
        this.infoMessage = 'Le lien vers les signalements de ce projet a été copié.';
      }, (error) => console.error('Failed to copy link: ', error));
    },

    copyFeaturesCode() {
      const webComponent = `
      <!-- Pour modifier la police, ajoutez l'attribut "font" avec le nom de la police souhaitée (par exemple: font="'Roboto Condensed', Lato, 'Helvetica Neue'"). -->
      <!-- Dans le cas où la police souhaitée ne serait pas déjà disponible dans la page affichant le web component, incluez également une balise <style> pour l'importer. -->
      <style>@import url('https://fonts.googleapis.com/css?family=Roboto Condensed:400,700,400italic,700italic&subset=latin');</style>
      <scr` + `ipt src="${this.configuration.VUE_APP_DJANGO_BASE}/geocontrib/static/wc/project-preview.js"></scr` + `ipt>
      <project-preview
        domain="${this.configuration.VUE_APP_DJANGO_BASE}"
        project-slug="${this.project.slug}"
        color="${this.configuration.VUE_APP_PRIMARY_COLOR}"
        font="${this.configuration.VUE_APP_FONT_FAMILY}"
        width=""
      ></project-preview>`;

      navigator.clipboard.writeText(webComponent).then(() => {
        this.infoMessage = 'Le web component des signalements de ce projet a été copié.';
      }, (error) => console.error('Failed to copy link: ', error));
    },
  }
};

</script>

<style lang="less" scoped>
.project-card {
  display: flex;
  gap: 1.25rem;
  padding: 1.25rem 0;
  border-bottom: 1px solid #e5e9f0;
  background: #fff;
}

.project-card--standalone {
  border-bottom: 0;
  padding: 1rem 0.25rem;
}

.project-card__visual {
  flex: 0 0 9.5rem;
  width: 9.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.project-card__thumb {
  display: block;
  width: 100%;
  height: 9.5rem;
  overflow: hidden;
  background: #eef2f7;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.project-card__stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem;
  min-height: 2rem;
  padding: 0.35rem 0.6rem;
  border-radius: 3px;
  background: #3A468E;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    white-space: nowrap;
  }

  .icon,
  .stat-icon {
    margin: 0;
    width: 1rem;
    height: 1rem;
    object-fit: contain;
  }
}

.project-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.project-card__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.project-card__copy {
  height: 9.5rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.project-card__title {
  display: block;
  color: var(--header-color, #1f3460);
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 0.25rem;

  &:hover {
    color: var(--primary-color, #008c86);
  }
}

.project-card__date {
  margin: 0 0 0.65rem;
  color: #8a93a3;
  font-size: 0.85rem;
}

.project-card__description {
  flex: 1;
  min-height: 0;
  overflow: hidden;

  .preview {
    max-height: 100%;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    color: #4b5565;
    font-size: 0.95rem;
    line-height: 1.45;
  }

  /deep/ p {
    margin: 0;
    display: inline;
    text-align: left;
  }

  /deep/ p + p::before {
    content: ' ';
  }
}

.project-card__actions {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-height: 2rem;
  margin-top: 0.75rem;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.35rem;
  height: 1.35rem;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;

  img {
    width: 1.15rem;
    height: 1.15rem;
    object-fit: contain;
  }
}

.copy-modal {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}

.copy-modal__panel {
  width: min(28rem, calc(100% - 2rem));
  padding: 1.25rem 1.4rem;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);

  p {
    margin: 0 0 1rem;
    color: #1b2142;
    font-size: 1rem;
    line-height: 1.4;
  }
}

.project-card__badges {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;
  max-width: 16rem;
}

.badge-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.4rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.7rem;
  border-radius: 3px;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.badge-icon {
  display: block;
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  object-fit: contain;
}

.tone-consultation {
  background: #3A468E;
}

.tone-moderation {
  background: #7038B0;
}

.tone-attribute {
  background: #139BEF;
}

.tone-info {
  background: #F2F3F5;
  color: #1B2142;
}

.tone-draft {
  background: #F8C272;
  color: #1B2142;
}

.tone-pending {
  background: #F09070;
  color: #1B2142;
}

@media screen and (max-width: 900px) {
  .project-card__body {
    flex-direction: column;
  }

  .project-card__badges {
    align-items: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
    max-width: none;
  }
}

@media screen and (max-width: 700px) {
  .project-card {
    flex-direction: column;
    gap: 0.85rem;
  }

  .project-card__visual {
    width: 100%;
    flex-basis: auto;
  }

  .project-card__thumb {
    height: auto;
    aspect-ratio: 16 / 9;
  }

  .project-card__copy {
    height: auto;
  }

  .project-card__badges {
    order: -1;
  }
}
</style>
